import ServerServices from "@/components/ServerServices";
import { useEffect, useState } from "react";
import { SlidersHorizontal, Power, RefreshCw, Server, Radio, Cpu } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Loading, fmtUptime, fmtBytes } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-800/70 py-2.5 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</span>
      <span className="font-mono text-sm text-zinc-200">{value}</span>
    </div>
  );
}

export default function ServerControl() {
  const [info, setInfo] = useState(null);
  const { user } = useAuth();
  const canOwnerAdmin = user?.role === "owner" || user?.role === "admin";

  const load = () => api.get("/server/info").then(setInfo).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const togglePublic = async (v) => {
    try { await api.post("/server/public-serving", { enabled: v }); toast.success(v ? "Public serving resumed" : "Public serving paused"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const restart = async () => {
    try { const r = await api.post("/server/restart"); toast.success(r.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!info) return <Loading label="Reading server info" />;
  const s = info.system;

  return (
    <div data-testid="server-page">
      <PageHeader title="Server Control" breadcrumb="cannon / server" subtitle="System info and public serving control" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3"><Server className="h-3.5 w-3.5" /> System Info</div>
          <Row label="Version" value={info.version} />
          <Row label="Base Domain" value={info.base_domain} />
          <Row label="Bind" value={info.bind} />
          <Row label="Mode" value={info.mode} />
          <Row label="Python" value={info.python} />
          <Row label="Operating system" value={info.platform} />
          <Row label="Server" value={s.server_id||"Local instance"} />
          <Row label="Free disk" value={s.disk_free_gb==null?"unavailable":s.disk_free_gb+" GB"} />
          <Row label="Uptime" value={fmtUptime(s.uptime_seconds)} />
          <Row label="CPU / RAM / Disk" value={`${s.cpu_percent}% / ${s.mem_percent}% / ${s.disk_percent}%`} />
        </div>

        <div className="space-y-4">
          <div className="border border-zinc-800 rounded-lg bg-[#0c0c0e] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4"><Radio className="h-3.5 w-3.5" /> Public Serving</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-zinc-200">Serve sites publicly via Nginx</div>
                <div className="font-mono text-xs text-zinc-500 mt-0.5">{info.public_serving ? "Currently serving" : "Paused — Nginx returns maintenance"}</div>
              </div>
              <Switch checked={info.public_serving} onCheckedChange={togglePublic} disabled={!canOwnerAdmin} data-testid="public-serving-switch" />
            </div>
          </div>

          <div className="border border-rose-500/20 rounded-lg bg-rose-500/5 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-rose-400 mb-3"><Power className="h-3.5 w-3.5" /> Danger Zone</div>
            <div className="flex items-center justify-between">
              <div><div className="text-sm text-zinc-200">Restart Cannon</div><div className="font-mono text-xs text-zinc-500 mt-0.5">Managed by supervisor / systemd on VPS</div></div>
              {user?.role === "owner" ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild><button data-testid="restart-cannon-btn" className="inline-flex items-center gap-2 rounded-md border border-rose-500/30 text-rose-300 px-3 py-1.5 text-sm hover:bg-rose-500/10"><RefreshCw className="h-4 w-4" /> Restart</button></AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0c0c0e] border-zinc-800">
                    <AlertDialogHeader><AlertDialogTitle>Restart Cannon?</AlertDialogTitle><AlertDialogDescription>Sends a restart signal to the process supervisor. Brief downtime expected.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={restart} className="bg-rose-600 hover:bg-rose-500">Restart</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : <span className="text-xs text-zinc-600 font-mono">owner only</span>}
            </div>
          </div>
        </div>
      </div>
      <ServerServices />
    </div>
  );
}
